import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import NoManufacturerAccounts from 'src/assets/no-manufacturer-accounts.svg';
import Avatar from 'src/components/ui/Avatar/Avatar';
import Button from 'src/components/ui/Button/Button';
import EmptyState from 'src/components/ui/EmptyState/EmptyState';
import PageHeader from 'src/components/ui/PageHeader/PageHeader';
import Switch from 'src/components/ui/Switch/Switch';
import Table from 'src/components/ui/Table/Table';
import {
  TableHeader,
  TableQueryParameters,
} from 'src/components/ui/Table/Table.interface';
import TablePagination from 'src/components/ui/Table/TablePagination/TablePagination';
import { paginationDefaultState } from 'src/constants/paginationConstants';
import { routes } from 'src/routes';
import {
  changeManufacturerAccountStatus,
  getManufacturerAccountList,
  setManufacturerAccountList,
} from 'src/store/actions/manufacturerAccountActions';
import { ManufacturerAccount } from 'src/store/models/ManufacturerAccount';
import { useLoadingSelector } from 'src/utils/useLoadingSelector';
import { useQueryParams } from 'src/utils/useQueryParams';

import classes from './ManufacturerAccounts.module.scss';

const ManufacturerAccounts: FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const manufacturerAccountList = useAppSelector(
    (state) => state.manufacturerAccount.manufacturerAccountList
  );
  const loading = useLoadingSelector({
    actionType: getManufacturerAccountList.type,
  });

  const headers: TableHeader<ManufacturerAccount>[] = [
    {
      accessor: 'manufacturer',
      label: t('common:company'),
      sortKey: 'manufacturerTitle',
      sortable: true,
      component: (account) => (
        <Avatar
          title={account?.manufacturer?.title}
          image={account?.image}
          imageSize={32}
          className={classes.Avatar}
        />
      ),
    },
    {
      accessor: 'manufacturer',
      label: t('settings:contact_person'),
      sortable: true,
      sortKey: 'manufacturerContactPerson',
      component: (account) => account?.manufacturer?.contactPerson,
    },
    {
      accessor: 'manufacturer',
      label: t('common:email'),
      sortable: true,
      sortKey: 'manufacturerEMail',
      component: (account) => account?.manufacturer?.eMail,
    },
    {
      accessor: 'manufacturer',
      label: t('common:phone'),
      sortable: true,
      sortKey: 'manufacturerPhone',
      component: (account) => account?.manufacturer?.phone,
    },
    {
      accessor: 'manufacturer',
      label: t('common:service_accounts'),
      component: (account) =>
        `${account?.manufacturer?.serviceAccountCount ?? 0}/${
          account?.manufacturer?.serviceAccountLimit
        }`,
    },
    {
      accessor: 'enabled',
      label: t('common:active'),
      sortable: true,
      sortKey: 'manufacturerEnabled',
      component: (account) => (
        <Switch
          checked={account?.enabled}
          handleChange={(_, checked) =>
            dispatch(
              changeManufacturerAccountStatus({
                manufacturer: account,
                enabled: checked,
              })
            )
          }
          loading={{
            actionType: changeManufacturerAccountStatus.type,
            id: account?.id,
          }}
        />
      ),
    },
  ];

  const { urlParams } = useQueryParams<TableQueryParameters>(location.search, {
    ...paginationDefaultState,
  });

  useEffect(() => {
    dispatch(setManufacturerAccountList({ count: 0, data: [], ctx: null }));
    return () => {
      dispatch(setManufacturerAccountList({ count: 0, data: [], ctx: null }));
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(getManufacturerAccountList({ params: urlParams }));
    // eslint-disable-next-line
  }, [location]);

  return (
    <div className={classes.Container}>
      <PageHeader
        title={t('common:manufacturer_accounts')}
        endNode={
          <Button
            onClick={() => navigate(routes.NEW_MANUFACTURER_ACCOUNT)}
            endIcon={<FontAwesomeIcon icon={faPlus} />}
            className={classes.HeaderButton}
          >
            {t('common:create_new')}
          </Button>
        }
      />
      <Table
        headers={headers}
        data={manufacturerAccountList?.data}
        search
        loading={loading}
        className={classNames(classes.Table, { [classes.Loading]: loading })}
        classes={{ header: classes.TableHeader }}
        onRowClick={(row) =>
          navigate(`${routes.EDIT_MANUFACTURER_ACCOUNT}/${row?.id}`)
        }
        emptyTableComponent={
          <EmptyState
            title={t('manufacturer_account:no_accounts')}
            subtitle={t('manufacturer_account:no_accounts_details')}
            icon={NoManufacturerAccounts}
          />
        }
        searchPlaceholder={t('common:search_field')}
      />
      <TablePagination
        className={classes.Pagination}
        urlParams={urlParams}
        total={manufacturerAccountList?.count}
      />
    </div>
  );
};

export default ManufacturerAccounts;
